import React from 'react';
import { useParams } from 'react-router-dom';
import {
  useCompleteAnonymousPhoneVerificationMutation,
  useStartAnonymousPhoneVerificationMutation,
} from 'apollo/generated/client-operations';
import { getGraphQLError, hasGraphQLErrorMessage } from 'utils/error';
import { AuthContext } from 'auth';
import { AlertService } from 'components/alertService';
import { Timer } from 'components/timer';
import { CodeInput } from 'components/codeInput';
import {
  SelfOnboardingStepProps,
  SelfOnboardingStepKey,
} from 'containers/selfOnboarding/steps/types';
import { Copy } from 'containers/selfOnboarding/steps/utils';
import {
  clearVerificationPhoneNumber,
  readVerificationPhoneNumber,
} from 'containers/selfOnboarding/helpers/verificationPhoneStorage';
import { getPhoneNumberAsE164, isStringEnumValue } from 'utils';

/**
 * Authenticated-flow OTP challenge (pre-account). Completes the accountless OTP:
 * an existing account is signed in and either resumed at its persisted step (if
 * already bound to a jobsite) or sent to contractor selection to bind; a
 * brand-new phone hands back a token stashed for account creation. Sign-in only
 * — jobsite assignment happens later at contractor selection, so new and unbound
 * existing workers converge there.
 */
export function AuthLoginCompleteStep(
  props: SelfOnboardingStepProps,
): React.ReactElement {
  const { state, updateState, navigation, localize, language, refetchData } =
    props;
  const { verificationPhoneNumber } = state;
  const { goToStep } = navigation;
  const { jobsiteInvitationId } = useParams<{ jobsiteInvitationId: string }>();

  const { authState } = React.useContext(AuthContext);
  const [isSaving, setIsSaving] = React.useState(false);
  const [timerResetKey, setTimerResetKey] = React.useState(0);
  const [codeResetKey, setCodeResetKey] = React.useState(0);

  const resetTimer = (): void => setTimerResetKey((key) => key + 1);
  const resetCode = (): void => setCodeResetKey((key) => key + 1);

  const [completeAnonymousPhoneVerification] =
    useCompleteAnonymousPhoneVerificationMutation();
  const [startAnonymousPhoneVerification] =
    useStartAnonymousPhoneVerificationMutation();

  const onSubmit = async (verificationCode: string): Promise<void> => {
    if (isSaving) {
      return;
    }
    setIsSaving(true);

    try {
      const { data } = await completeAnonymousPhoneVerification({
        variables: { input: { verificationCode, jobsiteInvitationId } },
      });
      const { success, token, auth, selfOnboardingCurrentStep } =
        data.completeAnonymousPhoneVerification ?? {};
      setIsSaving(false);

      // OTP accepted — the staged phone number is no longer needed.
      if (auth || (success && token)) {
        clearVerificationPhoneNumber();
      }

      if (auth) {
        // Existing account: sign in and set userAccountId (whitelisting the
        // post-account steps), then reload the session so the container
        // rebuilds full onboarding state.
        authState.signIn(auth.session.sessionToken, null);
        updateState({ userAccountId: auth.user.userAccountId });
        await refetchData();

        // The sign-in response reports the worker's step scoped to *this*
        // jobsite: a value means they are already bound and resuming; null
        // means they are not bound here yet (new, or bound only elsewhere).
        const isResumingBoundWorker =
          !!selfOnboardingCurrentStep &&
          isStringEnumValue(SelfOnboardingStepKey, selfOnboardingCurrentStep);

        // For a resuming worker, do NOT navigate imperatively: once the
        // refetched session rebuilds state (worker + jobsiteWorker), the
        // navigation hook fast-forwards from here to the persisted step — and
        // mounts that step only after full state is ready, which avoids both a
        // whitelist bounce and a half-rendered step. Racing it with a manual
        // goToStep is what caused those. Only an unbound worker needs a push to
        // contractor selection to bind (Mutation B); new and unbound existing
        // workers converge there.
        if (!isResumingBoundWorker) {
          goToStep(SelfOnboardingStepKey.ContractorSelection);
        }
      } else if (success && token) {
        // Brand-new phone: stash the OTP token (consumed by
        // selfOnboardingCreateAccount) and advance to account creation.
        updateState({ phoneVerificationToken: token });
        goToStep(SelfOnboardingStepKey.WorkerName);
      }
    } catch (error) {
      setIsSaving(false);
      if (
        hasGraphQLErrorMessage(
          error,
          'Unexpected error value: { statusCode: 404 }',
        )
      ) {
        AlertService.alert('danger', 'Something went wrong!', 'Invalid code');
      } else {
        AlertService.alert(
          'danger',
          'Something went wrong!',
          getGraphQLError(error),
        );
      }
    }
  };

  const requestAnotherCode = async (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
  ): Promise<void> => {
    e.preventDefault();

    try {
      // On a reload the in-memory phone number is gone; fall back to the
      // tab-scoped copy. If neither is present, send the worker back to re-enter
      // it rather than firing the mutation with an empty input.
      const phoneNumber =
        verificationPhoneNumber ?? readVerificationPhoneNumber();
      if (!phoneNumber) {
        goToStep(SelfOnboardingStepKey.Login);
        return;
      }
      const e164PhoneNumber = getPhoneNumberAsE164(phoneNumber);
      const result = await startAnonymousPhoneVerification({
        variables: { input: { phoneNumber: e164PhoneNumber } },
      });
      if (result.data.startAnonymousPhoneVerification.success) {
        resetTimer();
        resetCode();
      }
    } catch (error) {
      AlertService.alert(
        'danger',
        'Something went wrong!',
        getGraphQLError(error),
      );
    }
  };

  return (
    <div>
      <h2 className="odin-text-1.5xl odin-text-odin-black odin-mt-7.5 odin-mb-6 odin-text-center">
        {localize(Copy.self_onboarding_confirm_phone_title)}
      </h2>
      <CodeInput
        onCodeFilled={onSubmit}
        onInvalidCodeFormat={(code: string): void => {
          AlertService.alert('danger', 'Invalid code format!', `"${code}"`);
        }}
        resetKey={codeResetKey}
      />
      <div className="odin-pt-10">
        <Timer
          language={language}
          textCopy={Copy.self_onboarding_confirm_phone_time_to_complete}
          onTimeout={(): void => goToStep(SelfOnboardingStepKey.Login)}
          resetKey={timerResetKey}
        />
      </div>
      <div className="odin-pt-6.5 odin-px-5 odin-text-sm odin-text-center">
        {localize(Copy.self_onboarding_confirm_phone_instruction)}{' '}
        <a href="" onClick={requestAnotherCode}>
          {localize(Copy.self_onboarding_confirm_phone_request_another_code)}
        </a>
      </div>
    </div>
  );
}
